/**
 * Product Variant Components
 * Depends on: global-core.js (for requestIdleCallback polyfill)
 */

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.domCache = {};
    this.rafId = null;
    this.debounceTimer = null;
    this.addEventListener('change', this.onVariantChange.bind(this));
  }

  onVariantChange(event) {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.handleVariantChange();
    }, 50);
  }

  handleVariantChange() {
    this.updateOptions();
    this.updateMasterId();

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }

    this.rafId = requestAnimationFrame(() => {
      this.toggleAddButton(true, '', false);
      this.removeErrorMessage();

      if (!this.currentVariant) {
        this.toggleAddButton(true, '', true);
        this.setUnavailable();
      } else {
        this.batchDOMUpdates();
      }
    });
  }

  batchDOMUpdates() {
    this.updateMedia();
    this.updateURL();
    this.updateVariantInput();
    this.updateShareUrl();

    requestIdleCallback(
      () => {
        this.updateVariantStatuses();
        this.updatePickupAvailability();
      },
      { timeout: 100 }
    );

    this.renderProductInfo();
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant?.featured_media) return;

    if (!this.domCache.mediaGalleries) {
      this.domCache.mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);
    }

    this.domCache.mediaGalleries.forEach((mediaGallery) =>
      mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true)
    );

    if (!this.domCache.modalContent) {
      this.domCache.modalContent = document.querySelector(
        `#ProductModal-${this.dataset.section} .product-media-modal__content`
      );
    }

    if (!this.domCache.modalContent) return;

    const newMediaModal = this.domCache.modalContent.querySelector(
      `[data-media-id="${this.currentVariant.featured_media.id}"]`
    );
    if (newMediaModal) {
      this.domCache.modalContent.prepend(newMediaModal);
    }
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    if (!this.domCache.shareButton) {
      this.domCache.shareButton = document.getElementById(`Share-${this.dataset.section}`);
    }

    if (!this.domCache.shareButton?.updateUrl) return;
    this.domCache.shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    if (!this.domCache.productForms) {
      this.domCache.productForms = document.querySelectorAll(
        `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
      );
    }

    this.domCache.productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      if (!input) return;

      input.value = this.currentVariant.id;
      this.autoSubmitQuickAddForm(input);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    if (!this.domCache.variantSelected) {
      this.domCache.variantSelected = document.querySelector('#ProductInfo-quick-add .js-variant-selected');
      this.domCache.productFormButtons = document.querySelector('.product-form__buttons');
    }

    if (this.domCache.variantSelected) {
      let option_title = this.currentVariant.option1;
      if (this.currentVariant.option2) {
        option_title = this.currentVariant.option2;
      }
      this.domCache.variantSelected.textContent = option_title;
      this.domCache.productFormButtons?.classList.add('selected');
    }
  }

  autoSubmitQuickAddForm(input) {
    if (window.innerWidth <= 768) return;
    const form = document.querySelector('#ProductInfo-quick-add');
    if (!form || form.closest('.rocket-quickadd-drawer')) return;
    input.addEventListener('change', () => {
      const submitBtn = form.querySelector('#product-form-quick-add');
      if (submitBtn) {
        submitBtn.dispatchEvent(
          new Event('submit', {
            bubbles: true,
            cancelable: true,
          })
        );
      }
    });
  }

  updateVariantStatuses() {
    const checkedInput = this.querySelector(':checked');
    if (!checkedInput) return;

    const selectedValue = checkedInput.value;

    if (!this.domCache.inputWrappers) {
      this.domCache.inputWrappers = [...this.querySelectorAll('.product-form__input')];
    }

    const selectedOptionOneVariants = this.variantData.filter((variant) => selectedValue === variant.option1);

    this.domCache.inputWrappers.forEach((option, index) => {
      if (index === 0) return;

      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
      const previousOptionSelected = this.domCache.inputWrappers[index - 1].querySelector(':checked')?.value;

      if (!previousOptionSelected) return;

      const availableValues = [];
      for (let i = 0; i < selectedOptionOneVariants.length; i++) {
        const variant = selectedOptionOneVariants[i];
        if (variant.available && variant[`option${index}`] === previousOptionSelected) {
          availableValues.push(variant[`option${index + 1}`]);
        }
      }

      this.setInputAvailability(optionInputs, availableValues);
    });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.innerText = input.getAttribute('value');
      } else {
        input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', input.getAttribute('value'));
      }
    });
  }

  updatePickupAvailability() {
    if (!this.domCache.pickUpAvailability) {
      this.domCache.pickUpAvailability = document.querySelector('pickup-availability');
    }

    if (!this.domCache.pickUpAvailability) return;

    if (this.currentVariant?.available) {
      this.domCache.pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      this.domCache.pickUpAvailability.removeAttribute('available');
      this.domCache.pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm && typeof productForm.handleErrorMessage === 'function') {
      productForm.handleErrorMessage();
    }
  }

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.originalSection || this.dataset.section;

    fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        if (this.currentVariant.id !== requestedVariantId) return;

        requestAnimationFrame(() => {
          this.updateProductInfoDOM(responseText, sectionId);
        });
      });
  }

  updateProductInfoDOM(responseText, sectionId) {
    const html = new DOMParser().parseFromString(responseText, 'text/html');

    if (!this.domCache.priceDestination) {
      this.domCache.priceDestination = document.getElementById(`price-${this.dataset.section}`);
      this.domCache.skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
      this.domCache.inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);
    }

    const sourceSection = this.dataset.originalSection || this.dataset.section;
    const priceSource = html.getElementById(`price-${sourceSection}`);
    const skuSource = html.getElementById(`Sku-${sourceSection}`);
    const inventorySource = html.getElementById(`Inventory-${sourceSection}`);

    const updates = [];

    if (priceSource && this.domCache.priceDestination) {
      updates.push(() => {
        this.domCache.priceDestination.innerHTML = priceSource.innerHTML;
        this.domCache.priceDestination.classList.remove('visibility-hidden');
      });
    }

    if (inventorySource && this.domCache.inventoryDestination) {
      updates.push(() => {
        this.domCache.inventoryDestination.innerHTML = inventorySource.innerHTML;
        this.domCache.inventoryDestination.classList.toggle('visibility-hidden', inventorySource.innerText === '');
      });
    }

    if (skuSource && this.domCache.skuDestination) {
      updates.push(() => {
        this.domCache.skuDestination.innerHTML = skuSource.innerHTML;
        this.domCache.skuDestination.classList.toggle(
          'visibility-hidden',
          skuSource.classList.contains('visibility-hidden')
        );
      });
    }

    updates.forEach((update) => update());

    const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`);
    this.toggleAddButton(
      addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
      window.variantStrings.soldOut
    );

    if (typeof publish === 'function') {
      publish(PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId,
          html,
          variant: this.currentVariant,
        },
      });
    }
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    if (!this.domCache.productForm) {
      this.domCache.productForm = document.getElementById(`product-form-${this.dataset.section}`);
    }

    if (!this.domCache.productForm) return;

    if (!this.domCache.addButton) {
      this.domCache.addButton = this.domCache.productForm.querySelector('[name="add"]');
      this.domCache.addButtonText = this.domCache.productForm.querySelector('[name="add"] > span');
    }

    if (!this.domCache.addButton) return;

    if (disable) {
      this.domCache.addButton.setAttribute('disabled', 'disabled');
      if (text && this.domCache.addButtonText) {
        this.domCache.addButtonText.textContent = text;
      }
    } else {
      this.domCache.addButton.removeAttribute('disabled');
      if (this.domCache.addButtonText) {
        this.domCache.addButtonText.textContent = window.variantStrings.addToCart;
      }
    }
  }

  setUnavailable() {
    if (!this.domCache.productForm) {
      this.domCache.productForm = document.getElementById(`product-form-${this.dataset.section}`);
    }

    if (!this.domCache.addButton) {
      this.domCache.addButton = this.domCache.productForm?.querySelector('[name="add"]');
      this.domCache.addButtonText = this.domCache.productForm?.querySelector('[name="add"] > span');
    }

    if (!this.domCache.priceDestination) {
      this.domCache.priceDestination = document.getElementById(`price-${this.dataset.section}`);
      this.domCache.inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);
      this.domCache.skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
    }

    if (!this.domCache.addButton) return;

    if (this.domCache.addButtonText) {
      this.domCache.addButtonText.textContent = window.variantStrings.unavailable;
    }

    if (this.domCache.priceDestination) this.domCache.priceDestination.classList.add('visibility-hidden');
    if (this.domCache.inventoryDestination) this.domCache.inventoryDestination.classList.add('visibility-hidden');
    if (this.domCache.skuDestination) this.domCache.skuDestination.classList.add('visibility-hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();

    if (this.querySelectorAll('.cs-open-modal-outstock').length > 0) {
      this.querySelectorAll('.cs-open-modal-outstock').forEach((elm) => {
        elm.addEventListener('click', () => {
          if (this.closest('.product-card-wrapper').querySelector('.restock-rocket-button')) {
            this.closest('.product-card-wrapper').querySelector('.restock-rocket-button').click();
          }
        });
      });
    }
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.classList.remove('disabled');
      } else {
        input.classList.add('disabled');
      }
    });
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

customElements.define('variant-radios', VariantRadios);

class RocketBtnSizeGuide extends HTMLElement {
  constructor() {
    super();

    this.kiwiBtn = null;
    this.isLoading = false;

    this.addEventListener('click', this.handleClick.bind(this), { passive: true });
  }

  handleClick(e) {
    if (this.isLoading) return;
    this.isLoading = true;

    this.classList.add('loading');

    if (!this.kiwiBtn) {
      this.kiwiBtn = document.querySelector('.ks-chart-float-button');
    }

    if (this.kiwiBtn) {
      requestAnimationFrame(() => {
        this.kiwiBtn.click();

        setTimeout(() => {
          this.isLoading = false;
          this.classList.remove('loading');
        }, 500);
      });
    } else {
      this.isLoading = false;
      this.classList.remove('loading');
    }
  }
}

customElements.define('rocket-btn-size-guide', RocketBtnSizeGuide);

class RocketCsVariantSelected extends HTMLElement {
  constructor() {
    super();

    this.selectedText = this.dataset.textSelected;
    this.btnOpenOptions = this.querySelector('.product-variant-select-cs--btn') || null;
    this.elmOptions = this.querySelector('.product-variant-select-cs--list') || null;
    this.productData = JSON.parse(this.querySelector('[type="application/json"]').textContent) || null;
    this.options = this.querySelectorAll('.select__select li:not(.disabled)');
    this.elmText = this.btnOpenOptions?.querySelector('span');
    this.overlay = this.querySelector('.size-overlay-custom') || null;
    this.hasSelected = false;

    if (this.btnOpenOptions) {
      this.btnOpenOptions.addEventListener('click', () => {
        this.elmOptions?.classList.toggle('hidden');
        this.overlay?.classList.toggle('hidden');
        this.btnOpenOptions?.classList.toggle('open');
      });
    }
  }

  connectedCallback() {
    this.handleSelectedOptions();
    this.handleClickOutside();
    this.handleOverlayClick();

    if (window.innerWidth <= 768) {
      this.selectFirstOption();
    }
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  handleClickOutside() {
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.closeSelected();
      }
    });
  }

  handleOverlayClick() {
    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.closeSelected();
      });
    }
  }

  handleResize() {
    if (window.innerWidth <= 768 && !this.hasSelected) {
      this.selectFirstOption();
    }
  }

  selectFirstOption() {
    if (this.options.length > 0) {
      const firstOption = this.options[0];
      const optionValue = firstOption.getAttribute('value');

      if (optionValue) {
        this.triggerVariantChange(optionValue);
        this.closeSelected();
        this.hasSelected = true;
      }
    }
  }

  handleSelectedOptions() {
    if (this.options.length > 0) {
      this.options.forEach((item) => {
        item.addEventListener('click', () => {
          const optionValue = item.getAttribute('value');
          this.triggerVariantChange(optionValue);
          this.elmText.textContent = optionValue;
          this.closeSelected();
          this.hasSelected = true;
        });
      });
    }
  }

  closeSelected() {
    this.elmOptions?.classList.add('hidden');
    this.btnOpenOptions?.classList.remove('open');
    this.overlay?.classList.add('hidden');
  }

  triggerVariantChange(optionValue) {
    if (!this.variantSelectEl) {
      this.variantSelectEl = document.querySelector('variant-selects');
    }

    if (!this.variantSelectEl) return;

    if (!this.selectElements) {
      this.selectElements = this.variantSelectEl.querySelectorAll('select');
    }

    this.selectElements.forEach((select) => {
      const matchedOption = Array.from(select.options).find((opt) => opt.value === optionValue);
      if (matchedOption) {
        select.value = optionValue;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    requestIdleCallback(
      () => {
        if (!this.variantIdInput) {
          this.variantIdInput = document.querySelector('[name="id"]');
        }

        if (this.variantIdInput?.value) {
          this.classList.remove('variant-missing');
        }
      },
      { timeout: 100 }
    );
  }
}

customElements.define('rocket-cs-variant-selected', RocketCsVariantSelected);

class RocketProductCsTabs extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.tabTitles = this.querySelectorAll('.product-cs-tabs--titles span');
    this.tabContents = this.querySelectorAll('.product-cs-tabs--content');

    this.tabTitles.forEach((title, index) => {
      title.addEventListener('click', () => {
        this.setActiveTab(index);
      });
    });

    let activeIndex = Array.from(this.tabTitles).findIndex((title) => title.classList.contains('active'));
    if (activeIndex === -1) activeIndex = 0;

    this.setActiveTab(activeIndex);
  }

  setActiveTab(index) {
    this.tabTitles.forEach((title, i) => {
      title.classList.toggle('active', i === index);
    });

    this.tabContents.forEach((content, i) => {
      content.style.display = i === index ? 'block' : 'none';
    });
  }
}

customElements.define('rocket-product-cs-tabs', RocketProductCsTabs);

class RocketProductCompleteTheLook extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initSlider();
  }

  initSlider() {
    if (this.querySelector('.swiper')) {
      new Swiper(this.querySelector('.swiper'), {
        loop: false,
        spaceBetween: 2,
        slidesPerView: 'auto',
        mousewheel: {
          forceToAxis: true,
        },
        breakpoints: {
          768: {
            spaceBetween: 13,
          },
        },
      });
    }
  }
}

customElements.define('rocket-product-complete-the-look', RocketProductCompleteTheLook);

class RocketProductStickyButton extends HTMLElement {
  constructor() {
    super();
    this.domCache = {};
    this.initButton();
    this.initRestockWidgets();
  }

  initButton() {
    const stickyBtn = this.querySelector('.sticky_buy_buttons__custom-btn');
    if (!stickyBtn) return;

    stickyBtn.addEventListener('click', this.handleButtonClick.bind(this), { passive: true });
  }

  handleButtonClick(event) {
    if (!this.domCache.productForm) {
      this.domCache.productForm = document.querySelector('product-info product-form');
    }

    if (!this.domCache.variantIdInput) {
      this.domCache.variantIdInput = this.domCache.productForm?.querySelector('[name=id]');
    }

    if (!this.domCache.rocketVariantSelected) {
      this.domCache.rocketVariantSelected = this.querySelector('rocket-cs-variant-selected');
    }

    if (!this.domCache.variantIdInput?.value) {
      this.domCache.rocketVariantSelected?.classList.add('variant-missing');
      return;
    } else {
      this.domCache.rocketVariantSelected?.classList.remove('variant-missing');
    }

    if (!this.domCache.productForm?.querySelector('button.product-form__submit')) return;

    const stickyBtn = this.querySelector('.sticky_buy_buttons__custom-btn');
    const productID = stickyBtn.dataset.id;

    if (!this.domCache.productFormById) {
      this.domCache.productFormById = document.querySelector(`.product-form-${productID}`);
    }

    if (this.domCache.productFormById) {
      if (!this.domCache.loadingSpinner) {
        this.domCache.loadingSpinner = this.querySelector('.loading-overlay__spinner');
      }

      this.domCache.loadingSpinner?.classList.remove('hidden');
      this.classList.add('loading');

      this.domCache.productFormById.dispatchEvent(
        new Event('submit', {
          bubbles: true,
          cancelable: true,
        })
      );
    }
  }

  initRestockWidgets() {
    const restockWidgets = this.querySelectorAll('.nm-restock-widget');
    if (restockWidgets.length === 0) return;

    restockWidgets.forEach((elm) => {
      elm.addEventListener('click', this.handleRestockClick.bind(this, elm), { passive: true });
    });
  }

  handleRestockClick(elm) {
    try {
      if (!this.productDataCache) {
        const scriptTag = this.querySelector('script[type="application/json"]');
        this.productDataCache = scriptTag ? JSON.parse(scriptTag.textContent) : null;
      }

      const selectedValue = elm.closest('.disabled')?.getAttribute('value');
      if (!this.productDataCache || !selectedValue) return;

      const variantId = this.productDataCache.find((variant) => variant.option1 === selectedValue)?.id;

      if (variantId && window._RestockRocket) {
        window._RestockRocket.openModal(this.productDataCache, parseInt(variantId));
      }
    } catch (error) {}
  }
}

customElements.define('rocket-product-sticky-button', RocketProductStickyButton);

class RocketGifWrapToggle extends HTMLElement {
  constructor() {
    super();
    this.domCache = {};
    this.isProcessing = false;
  }

  connectedCallback() {
    this.checkbox = this.querySelector('#gift-packaging');
    if (!this.checkbox) return;

    this.variantId = this.checkbox.dataset.variantId;
    this.checkbox.addEventListener('change', this.handleCheckboxChange.bind(this), { passive: true });
  }

  handleCheckboxChange() {
    if (this.isProcessing) return;

    if (this.checkbox.checked) {
      this.addGiftWrap();
    } else {
      this.removeGiftWrap();
    }
  }

  async addGiftWrap() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: this.variantId, quantity: 1 }),
      });

      if (document.body.classList.contains('template--cart')) {
        location.reload();
      } else {
        await this.updateCartDrawerUI();
      }
    } catch (error) {
    } finally {
      this.isProcessing = false;
    }
  }

  async removeGiftWrap() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const item = cart.items.find((i) => i.variant_id == this.variantId);

      if (item) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });

        if (document.body.classList.contains('template--cart')) {
          location.reload();
        } else {
          await this.updateCartDrawerUI();
        }
      }
    } catch (error) {
    } finally {
      this.isProcessing = false;
    }
  }

  async updateCartDrawerUI() {
    try {
      const response = await fetch(`${routes.cart_url}`);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      requestAnimationFrame(() => {
        if (!this.domCache.currentCartDrawer) {
          this.domCache.currentCartDrawer = document.querySelector('cart-drawer');
          this.domCache.currentCartCount = document.querySelector('.cart-count-bubble');
        }

        const newCartDrawer = html.querySelector('cart-drawer');
        if (newCartDrawer && this.domCache.currentCartDrawer) {
          this.domCache.currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
          this.domCache.currentCartDrawer.classList.remove('is-empty');
        }

        const newCartCount = html.querySelector('.cart-count-bubble');
        if (newCartCount && this.domCache.currentCartCount) {
          this.domCache.currentCartCount.innerHTML = newCartCount.innerHTML;
        }
      });
    } catch (error) {}
  }
}

customElements.define('rocket-gif-wrap-toggle', RocketGifWrapToggle);
